import React, { useState, useEffect, useRef } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { api, HuntListing } from '../api';
import { GREEN_PLACEHOLDER } from '../components';
import { ImagePickerModal } from './ImagePickerModal';
import { DraggablePositionMap } from './DraggablePositionMap';

export function EditListingModal({listing,onClose,onSaved}:{listing:HuntListing;onClose:()=>void;onSaved:(l:HuntListing)=>void}){
  const {mode}=useThemeMode();const T=getTheme(mode);
  const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const lS:React.CSSProperties={display:'block',color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6};
  const bP:React.CSSProperties={background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'};
  const bSc:React.CSSProperties={background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:5,padding:'12px 24px',fontSize:14,cursor:'pointer',fontFamily:'inherit'};
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
  const secT:React.CSSProperties={color:T.textMuted,fontSize:11,fontWeight:600,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:16};

  const[form,setForm]=useState({
    title:listing.title,subtitle:listing.subtitle||'',description:listing.description||'',
    location:listing.location,region:listing.region,county:listing.county||'',
    primaryGameType:listing.primaryGameType,gameTypes:listing.gameTypes.join(', '),
    startDate:listing.startDate?.split('T')[0]||'',endDate:listing.endDate?.split('T')[0]||'',
    imageUrl:listing.imageUrl,price:String(listing.price),priceUnit:listing.priceUnit,
    spotsTotal:String(listing.spotsTotal),spotsAvailable:String(listing.spotsAvailable),
    difficulty:listing.difficulty,includes:listing.includes.join('\n'),highlights:listing.highlights.join('\n'),
    contactName:listing.contactName||'',contactEmail:listing.contactEmail||'',
    annualDrivenHunts:String(listing.annualDrivenHunts||0),afterhuntHuntersCount:String(listing.afterhuntHuntersCount||0),
    mapLat:String(listing.mapLat||''),mapLng:String(listing.mapLng||''),
    status:'',
  });
  const ALL_SPECIES:Record<string,string>={'älg':'Älg','dovhjort':'Dovhjort','kronhjort':'Kronhjort','rådjur':'Rådjur','råbock':'Råbock','vildsvin':'Vildsvin','fasan':'Fasan','björn':'Björn','lo':'Lo','räv':'Räv','hare':'Hare','and':'And','kanadagås':'Kanadagås','grågås':'Grågås','kricka':'Kricka','knipa':'Knipa','mink':'Mink','utter':'Utter','grävling':'Grävling'};
  const ALL_FACILITIES:Record<string,string>={slaktrum:'Slaktrum',kylrum:'Kylrum',eldstad:'Eldstad',atlar:'Åtlar',viltkameror:'Viltkameror',jaktkojor:'Jaktkoja',pass_torn:'Pass m. torn',pass_stol:'Pass m. stol',markpass:'Markpass',overnattning:'Övernattning',bastu:'Bastu',badtunna:'Badtunna'};
  const LVL_LABELS:Record<number,string>={0:'Ej vald',1:'Sällsynt',2:'Sparsam',3:'Förekommande',4:'Riklig',5:'Extrem'};
  const LVL_COLORS:Record<number,string>={0:T.border,1:'#5a5a5a',2:'#8a7a30',3:'#c5870a',4:'#e67e22',5:'#c0392b'};
  const[species,setSpecies]=useState<Record<string,number>>(()=>{try{return listing.speciesOccurrenceJson?JSON.parse(listing.speciesOccurrenceJson):{}}catch{return{}}});
  const[facilities,setFacilities]=useState<Record<string,number>>(()=>{try{return listing.facilitiesJson?JSON.parse(listing.facilitiesJson):{}}catch{return{}}});
  const[galleryUrls,setGalleryUrls]=useState<string[]>(listing.galleryUrls||[]);
  const[videoUrl,setVideoUrl]=useState(listing.videoUrl||'');
  const[saving,setSaving]=useState(false);
  const[error,setError]=useState('');
  const[uploading,setUploading]=useState(false);
  const[showImgPicker,setShowImgPicker]=useState(false);
  const[showGalleryPicker,setShowGalleryPicker]=useState(false);
  const imageRef=useRef<HTMLInputElement>(null);
  const galleryRef=useRef<HTMLInputElement>(null);
  const videoRef=useRef<HTMLInputElement>(null);

  const handleMainImage=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploading(true);
    try{const res=await api.uploadFile(file);setForm(f=>({...f,imageUrl:res.url}))}
    catch{setError('Bilduppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const handleGallery=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=e.target.files;if(!files?.length)return;
    setUploading(true);
    try{const res=await api.uploadFiles(Array.from(files));setGalleryUrls(prev=>[...prev,...res.files.map(f=>f.url)])}
    catch{setError('Bilduppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const handleVideo=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploading(true);
    try{const res=await api.uploadFile(file);setVideoUrl(res.url)}
    catch{setError('Videouppladdning misslyckades')}
    finally{setUploading(false);e.target.value=''}
  };
  const removeGalleryImg=(idx:number)=>setGalleryUrls(prev=>prev.filter((_,i)=>i!==idx));

  const handleSave=async()=>{
    setSaving(true);setError('');
    try{
      const input:any={
        title:form.title,subtitle:form.subtitle||undefined,description:form.description||undefined,
        location:form.location,region:form.region,county:form.county||undefined,
        primaryGameType:form.primaryGameType,
        gameTypes:form.gameTypes.split(',').map(s=>s.trim()).filter(Boolean),
        startDate:form.startDate,endDate:form.endDate||undefined,
        imageUrl:form.imageUrl,
        galleryUrls:galleryUrls.length?galleryUrls:undefined,
        videoUrl:videoUrl||undefined,
        price:parseFloat(form.price)||0,priceUnit:form.priceUnit,
        spotsTotal:parseInt(form.spotsTotal)||0,spotsAvailable:parseInt(form.spotsAvailable)||0,
        difficulty:form.difficulty,
        includes:form.includes.split('\n').filter(Boolean),
        highlights:form.highlights.split('\n').filter(Boolean),
        contactName:form.contactName||undefined,contactEmail:form.contactEmail||undefined,
        annualDrivenHunts:parseInt(form.annualDrivenHunts)||undefined,
        afterhuntHuntersCount:parseInt(form.afterhuntHuntersCount)||undefined,
        mapLat:form.mapLat?parseFloat(form.mapLat):undefined,
        mapLng:form.mapLng?parseFloat(form.mapLng):undefined,
        speciesOccurrenceJson:Object.keys(species).length?JSON.stringify(species):undefined,
        facilitiesJson:Object.keys(facilities).length?JSON.stringify(facilities):undefined,
      };
      const updated=await api.updateHuntListing(listing.id,input);
      onSaved(updated);onClose();
    }catch(ex:unknown){setError(ex instanceof Error?ex.message:'Något gick fel')}
    finally{setSaving(false)}
  };

  const u=(k:string,v:string)=>setForm(f=>({...f,[k]:v}));
  const fld=(label:string,key:string,opts?:{type?:string;rows?:number;placeholder?:string})=>(
    <div style={{marginBottom:14}}>
      <label style={lS}>{label}</label>
      {opts?.rows
        ?<textarea value={(form as any)[key]} onChange={e=>u(key,e.target.value)} rows={opts.rows} placeholder={opts?.placeholder} style={{...iS,resize:'vertical'}}/>
        :<input type={opts?.type||'text'} value={(form as any)[key]} onChange={e=>u(key,e.target.value)} placeholder={opts?.placeholder} style={iS}/>
      }
    </div>
  );

  return(
    <div style={{position:'fixed',inset:0,background:T.overlay,zIndex:950,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:8,width:'100%',maxWidth:720,maxHeight:'94vh',overflow:'auto',position:'relative'}}>
        {/* Header */}
        <div style={{position:'sticky',top:0,background:T.bg,borderBottom:`1px solid ${T.border}`,padding:'16px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',zIndex:10}}>
          <h2 style={{color:T.text,fontSize:18,fontWeight:800,margin:0,letterSpacing:'-0.02em'}}>Redigera jakt</h2>
          <button onClick={onClose} style={{background:'rgba(0,0,0,0.3)',border:'none',color:T.text,fontSize:18,width:36,height:36,borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>x</button>
        </div>
        <div style={{padding:'20px 24px 28px'}}>
          {error&&<div style={{background:'rgba(192,57,43,0.15)',border:'1px solid rgba(192,57,43,0.3)',borderRadius:4,padding:'10px 14px',color:'#ff6b6b',fontSize:13,marginBottom:16}}>{error}</div>}
          {uploading&&<div style={{background:T.amberDim,border:'1px solid rgba(238,143,9,0.25)',borderRadius:4,padding:'10px 14px',color:T.amber,fontSize:13,marginBottom:16}}>Laddar upp fil...</div>}

          {/* Grundinfo */}
          <div style={secT}>Grundinfo</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            {fld('Titel','title')}
            {fld('Undertitel','subtitle')}
            {fld('Beskrivning','description',{rows:4})}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Primärt viltslag','primaryGameType')}</div>
              <div>{fld('Vilttyper (kommaseparerade)','gameTypes')}</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Svårighetsgrad','difficulty',{placeholder:'Nybörjare / Medel / Erfaren'})}</div>
              <div>{fld('Status','status',{placeholder:'active'})}</div>
            </div>
          </div>

          {/* Plats & Datum */}
          <div style={secT}>Plats & Datum</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Plats','location')}</div>
              <div>{fld('Region','region')}</div>
            </div>
            {fld('Län','county')}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Startdatum','startDate',{type:'date'})}</div>
              <div>{fld('Slutdatum','endDate',{type:'date'})}</div>
            </div>
            <DraggablePositionMap lat={form.mapLat} lng={form.mapLng} onChange={(la,lo)=>{u('mapLat',la);u('mapLng',lo);}}/>
          </div>

          {/* Prissättning */}
          <div style={secT}>Prissättning</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Pris','price',{type:'number'})}</div>
              <div>{fld('Prisenhet','priceUnit',{placeholder:'per person'})}</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Totalt antal platser','spotsTotal',{type:'number'})}</div>
              <div>{fld('Lediga platser','spotsAvailable',{type:'number'})}</div>
            </div>
          </div>

          {/* Media */}
          <div style={secT}>Media</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            {showImgPicker&&listing.markId&&<ImagePickerModal markId={listing.markId} onPick={urls=>{u('imageUrl',urls[0]);setShowImgPicker(false)}} onUpload={()=>{setShowImgPicker(false);imageRef.current?.click()}} onClose={()=>setShowImgPicker(false)}/>}
            {showGalleryPicker&&listing.markId&&<ImagePickerModal markId={listing.markId} multiple onPick={urls=>{setGalleryUrls(p=>[...p,...urls.filter(x=>!p.includes(x))]);setShowGalleryPicker(false)}} onUpload={()=>{setShowGalleryPicker(false);galleryRef.current?.click()}} onClose={()=>setShowGalleryPicker(false)}/>}
            {/* Main image */}
            <div style={{marginBottom:16}}>
              <label style={lS}>Huvudbild</label>
              <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                {form.imageUrl&&<img src={form.imageUrl} alt="Huvudbild" style={{width:120,height:80,objectFit:'cover',borderRadius:4,border:`1px solid ${T.border}`}}/>}
                <div style={{flex:1}}>
                  <input value={form.imageUrl} onChange={e=>u('imageUrl',e.target.value)} placeholder="Bild-URL" style={{...iS,marginBottom:8}}/>
                  <input ref={imageRef} type="file" accept="image/*" onChange={handleMainImage} style={{display:'none'}}/>
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={()=>imageRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12}}>Ladda upp bild</button>
                    {listing.markId&&<button onClick={()=>setShowImgPicker(true)} style={{...bSc,padding:'8px 16px',fontSize:12,background:T.amberDim,border:`1px solid ${T.amber}`}}>Markens album</button>}
                  </div>
                </div>
              </div>
            </div>
            {/* Gallery */}
            <div style={{marginBottom:16}}>
              <label style={lS}>Galleri</label>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:8}}>
                {galleryUrls.map((url,i)=>(
                  <div key={i} style={{position:'relative',width:80,height:60}}>
                    <img src={url} alt={`Galleri ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:3,border:`1px solid ${T.border}`}}/>
                    <button onClick={()=>removeGalleryImg(i)} style={{position:'absolute',top:-6,right:-6,background:T.danger,color:T.onDanger,border:'none',width:20,height:20,borderRadius:'50%',fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',lineHeight:1}}>x</button>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:8}}>
                <input ref={galleryRef} type="file" accept="image/*" multiple onChange={handleGallery} style={{display:'none'}}/>
                <button onClick={()=>galleryRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12}}>+ Ladda upp bilder</button>
                {listing.markId&&<button onClick={()=>setShowGalleryPicker(true)} style={{...bSc,padding:'8px 16px',fontSize:12,background:T.amberDim,border:`1px solid ${T.amber}`}}>+ Från markens album</button>}
              </div>
            </div>
            {/* Video */}
            <div>
              <label style={lS}>Video</label>
              {videoUrl&&<div style={{marginBottom:8}}><video src={videoUrl} controls style={{width:'100%',maxHeight:180,borderRadius:4,border:`1px solid ${T.border}`}}/></div>}
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <input value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} placeholder="Video-URL" style={{...iS,flex:1}}/>
                <input ref={videoRef} type="file" accept="video/*" onChange={handleVideo} style={{display:'none'}}/>
                <button onClick={()=>videoRef.current?.click()} style={{...bSc,padding:'8px 16px',fontSize:12,whiteSpace:'nowrap'}}>Ladda upp</button>
                {videoUrl&&<button onClick={()=>setVideoUrl('')} style={{background:'transparent',border:'none',color:T.danger,fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>Ta bort</button>}
              </div>
            </div>
          </div>

          {/* Detaljer */}
          <div style={secT}>Detaljer</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            {fld('Highlights (en per rad)','highlights',{rows:3})}
            {fld('Ingår (en per rad)','includes',{rows:3})}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Drevjakter / år','annualDrivenHunts',{type:'number'})}</div>
              <div>{fld('Eftersöksjägare','afterhuntHuntersCount',{type:'number'})}</div>
            </div>
          </div>

          {/* Viltförekomst */}
          <div style={secT}>Viltförekomst</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <p style={{color:T.textSub,fontSize:12,marginTop:0,marginBottom:14}}>Markera vilka arter som finns på marken och ange hur vanliga de är (1-5)</p>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:'8px 20px'}}>
              {Object.entries(ALL_SPECIES).map(([key,label])=>{
                const lvl=species[key]||0;
                return(<div key={key} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0'}}>
                  <input type="checkbox" checked={lvl>0} onChange={e=>{
                    if(e.target.checked){setSpecies(s=>({...s,[key]:3}))}
                    else{setSpecies(s=>{const n={...s};delete n[key];return n})}
                  }} style={{accentColor:T.amber,width:16,height:16,cursor:'pointer'}}/>
                  <span style={{color:T.text,fontSize:13,minWidth:80}}>{label}</span>
                  {lvl>0&&(<>
                    <div style={{display:'flex',gap:2,flex:1}}>
                      {[1,2,3,4,5].map(n=>(
                        <div key={n} onClick={()=>setSpecies(s=>({...s,[key]:n}))} style={{flex:1,height:12,borderRadius:1,background:n<=lvl?(LVL_COLORS[lvl]??T.amber):T.border,cursor:'pointer',transition:'background 0.15s'}}/>
                      ))}
                    </div>
                    <span style={{color:LVL_COLORS[lvl]??T.textSub,fontSize:10,minWidth:76,textAlign:'right',fontWeight:600}}>{LVL_LABELS[lvl]}</span>
                  </>)}
                </div>);
              })}
            </div>
          </div>

          {/* Utrustning & Faciliteter */}
          <div style={secT}>Utrustning & Faciliteter</div>
          <div style={{...cS,marginBottom:20,padding:16}}>
            <p style={{color:T.textSub,fontSize:12,marginTop:0,marginBottom:14}}>Markera vilken utrustning och vilka faciliteter som finns på marken</p>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'8px 20px'}}>
              {Object.entries(ALL_FACILITIES).map(([key,label])=>{
                const count=facilities[key]||0;
                return(<div key={key} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0'}}>
                  <input type="checkbox" checked={count>0} onChange={e=>{
                    if(e.target.checked){setFacilities(f=>({...f,[key]:1}))}
                    else{setFacilities(f=>{const n={...f};delete n[key];return n})}
                  }} style={{accentColor:T.success,width:16,height:16,cursor:'pointer'}}/>
                  <span style={{color:T.text,fontSize:13,flex:1}}>{label}</span>
                  {count>0&&(
                    <div style={{display:'flex',alignItems:'center',gap:4}}>
                      <button onClick={()=>setFacilities(f=>({...f,[key]:Math.max(1,count-1)}))} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,width:24,height:24,borderRadius:2,cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>-</button>
                      <span style={{color:T.amber,fontSize:13,fontWeight:700,minWidth:20,textAlign:'center'}}>{count}</span>
                      <button onClick={()=>setFacilities(f=>({...f,[key]:count+1}))} style={{background:T.surface,border:`1px solid ${T.border}`,color:T.text,width:24,height:24,borderRadius:2,cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>+</button>
                    </div>
                  )}
                </div>);
              })}
            </div>
          </div>

          {/* Kontakt */}
          <div style={secT}>Kontakt</div>
          <div style={{...cS,marginBottom:24,padding:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div>{fld('Kontaktnamn','contactName')}</div>
              <div>{fld('Kontakt e-post','contactEmail',{type:'email'})}</div>
            </div>
          </div>

          {/* Actions */}
          <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
            <button onClick={onClose} style={{...bSc,padding:'12px 24px'}}>Avbryt</button>
            <button onClick={handleSave} disabled={saving||uploading} style={{...bP,padding:'12px 32px',opacity:(saving||uploading)?0.7:1,cursor:(saving||uploading)?'default':'pointer'}}>{saving?'Sparar...':'Spara ändringar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
